import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { Alert, Box, Button, FormControlLabel, Grid, Stack, Switch, Tooltip, Typography } from '@mui/material';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import DatePickerCustom from '../../components/DatePicker/DatePickerCustom';
import DailyBreakdownTable from './components/DailyBreakdownTable';
import { departmentIncomeTrackerTableHeader } from './utils/departmentIncomeTrackerTableHeader';
import DepartmentStatCard from './components/DepartmentStatCard';
import SummaryCardsGrid, { CARDS_GAP, MIN_CARD_WIDTH } from './components/SummaryCardsGrid';
import DepartmentShareDonut from './components/DepartmentShareDonut';
import BiggestDayCard from './components/BiggestDayCard';
import HighestDayByDepartment from './components/HighestDayByDepartment';
import { biggestRevenueDay, highestDayByDepartment } from './utils/incomeInsights';
import { cardsScale, useBesideHeight, useElementSize, useFitDensity, useFitToViewport } from './utils/layoutHooks';
import IncomeDetailDialog from './components/IncomeDetailDialog';
import { getDepartmentDailyCollection } from '../../redux/features/departmentIncomeTrackerSlice';
import {
  clearTempTarget,
  formatCurrency,
  formatPercent,
  getPaceStatus,
  getTempTarget,
  MONTH_TOTAL_ROW_ID,
  setTempTarget,
  sortDepartments,
  TAR_STATUS,
  TAR_STATUS_META,
} from './utils/deptDisplay';

// How many card-shaped placeholders hold the space while the very first answer is awaited (the real count - one per
// tracked department plus the Total - is only known once it arrives).
const PLACEHOLDER_CARDS = 8;

export default function DepartmentIncomeTrackerLanding() {
  const dispatch = useDispatch();
  const { responseData, loading } = useSelector((state) => state.departmentIncomeTracker.dailyCollection);

  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM-01'));
  // On: show ONLY SkillX (dept_id 35, not part of the default tracked set) instead of the
  // usual departments - an exclusive alternate view, not an addition to it.
  const [skillxOnly, setSkillxOnly] = useState(false);

  const fetchData = useCallback(() => {
    dispatch(getDepartmentDailyCollection({ month: selectedMonth, skillx: skillxOnly }));
  }, [dispatch, selectedMonth, skillxOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // How much of the month has actually happened - drives "on pace" status and which days
  // count toward the best/worst-day insights (a future zero-filled day isn't a "slow day").
  const { elapsedDays, daysInMonth, todayDate, isCurrentMonth } = useMemo(() => {
    const now = dayjs();
    const monthStart = dayjs(selectedMonth);
    const daysInMonth = monthStart.daysInMonth();
    let elapsedDays = daysInMonth;
    if (monthStart.isAfter(now, 'month')) elapsedDays = 0;
    else if (monthStart.isSame(now, 'month')) elapsedDays = now.date();
    return {
      elapsedDays,
      daysInMonth,
      todayDate: monthStart.isSame(now, 'month') ? now.format('YYYY-MM-DD') : null,
      isCurrentMonth: monthStart.isSame(now, 'month'),
    };
  }, [selectedMonth]);

  const departments = useMemo(() => sortDepartments(responseData?.meta?.departments || []), [responseData]);

  // "Try a target" previews, loaded fresh whenever the month/department set changes - only
  // for departments with no real target, and re-read from localStorage rather than carried
  // over in memory, so a department that gets a real target stops showing a stale preview.
  const [tempTargets, setTempTargets] = useState({});
  useEffect(() => {
    const next = {};
    departments.forEach((dept) => {
      if (dept.target === 0 && dept.deptId !== 0) {
        const stored = getTempTarget(dept.deptId, selectedMonth);
        if (stored !== null) next[dept.deptId] = stored;
      }
    });
    setTempTargets(next);
  }, [departments, selectedMonth]);

  const handleSetTemp = useCallback((deptId, value) => {
    setTempTarget(deptId, selectedMonth, value);
    setTempTargets((prev) => ({ ...prev, [deptId]: value }));
  }, [selectedMonth]);

  const handleClearTemp = useCallback((deptId) => {
    clearTempTarget(deptId, selectedMonth);
    setTempTargets((prev) => {
      const next = { ...prev };
      delete next[deptId];
      return next;
    });
  }, [selectedMonth]);

  const modifiedRows = useMemo(() => {
    if (!responseData?.data) return [];
    return responseData.data.map((row) => ({ ...row, ...(row.byDept || {}) }));
  }, [responseData?.data]);

  // Heatmap scale per department column - each column shaded relative to its own busiest
  // day, so Little Genie's ₹40K day reads as strong even next to OGD's ₹600K days.
  const maxByDept = useMemo(() => {
    const result = {};
    departments.forEach((dept) => {
      result[dept.deptId] = Math.max(0, ...modifiedRows.map((r) => r.byDept?.[dept.deptId] || 0));
    });
    return result;
  }, [departments, modifiedRows]);

  const maxTotal = useMemo(
    () => Math.max(0, ...modifiedRows.map((r) => r.total || 0)),
    [modifiedRows],
  );

  // Footer row: each department's and the Total Revenue column's sum for the whole month,
  // pinned to the bottom of the table via GlobalTableNoPagination's `permanentLastRow` (so it
  // stays put regardless of sort/search on the day rows above it). `undefined` while there's no
  // real data yet, so the table's own "No Data Found" state still shows instead of a lone
  // all-zero total row.
  const monthTotalsRow = useMemo(() => {
    if (!modifiedRows.length) return undefined;
    const totals = { date: MONTH_TOTAL_ROW_ID, dayLabel: 'Total' };
    departments.forEach((dept) => {
      totals[String(dept.deptId)] = modifiedRows.reduce(
        (sum, r) => sum + (Number(r[String(dept.deptId)]) || 0),
        0,
      );
    });
    totals.total = modifiedRows.reduce((sum, r) => sum + (Number(r.total) || 0), 0);
    return totals;
  }, [departments, modifiedRows]);

  // Which cell's transaction detail is open, if any - null when the dialog is closed. A click
  // on any department cell, the Unidentified cell, or the Total Revenue cell sets this.
  const [detailCell, setDetailCell] = useState(null);
  const handleCellClick = useCallback((date, deptId, deptName) => {
    setDetailCell({ date, deptId, deptName });
  }, []);

  const headers = useMemo(
    () => departmentIncomeTrackerTableHeader(departments, maxByDept, todayDate, maxTotal, handleCellClick),
    [departments, maxByDept, todayDate, maxTotal, handleCellClick],
  );

  // Exports exactly what the table shows (day rows + the month Total row), with raw numbers
  // rather than the formatted "₹ 1,23,456" strings so the sheet stays summable in Excel.
  const handleExport = useCallback(() => {
    const exportRows = monthTotalsRow ? [...modifiedRows, monthTotalsRow] : modifiedRows;
    const data = exportRows.map((row) =>
      headers.reduce((acc, col) => {
        acc[col.headerName] = col.field === 'dayLabel' ? row.dayLabel : Number(row[col.field]) || 0;
        return acc;
      }, {}),
    );
    const sheet = XLSX.utils.json_to_sheet(data, { header: headers.map((col) => col.headerName) });
    sheet['!cols'] = headers.map(() => ({ wch: 16 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Daily Breakdown');
    XLSX.writeFile(workbook, `department-income-${dayjs(selectedMonth).format('YYYY-MM')}${skillxOnly ? '-skillx' : ''}.xlsx`);
  }, [headers, modifiedRows, monthTotalsRow, selectedMonth, skillxOnly]);

  // Department pace status, and which one is furthest ahead/behind - only meaningful across
  // departments that actually have a target set (real or, for the preview, temporary).
  const departmentsWithPace = useMemo(() => {
    const withStatus = departments.map((dept) => {
      const isTemp = dept.target === 0 && !!tempTargets[dept.deptId];
      const effectiveTarget = dept.target > 0 ? dept.target : (tempTargets[dept.deptId] || 0);
      const status = getPaceStatus({ target: effectiveTarget, revenueToDate: dept.revenueToDate, elapsedDays, daysInMonth });
      const expectedToDate = effectiveTarget > 0 ? effectiveTarget * (elapsedDays / daysInMonth) : 0;
      const paceRatio = expectedToDate > 0 ? dept.revenueToDate / expectedToDate : null;
      const tar = effectiveTarget > 0 ? parseFloat(((dept.revenueToDate / effectiveTarget) * 100).toFixed(2)) : null;
      return { ...dept, target: effectiveTarget, tar, isTemp, status, paceRatio, expectedToDate };
    });

    const targeted = withStatus.filter((d) => d.paceRatio !== null);
    if (targeted.length >= 2) {
      const best = targeted.reduce((a, b) => (b.paceRatio > a.paceRatio ? b : a));
      const worst = targeted.reduce((a, b) => (b.paceRatio < a.paceRatio ? b : a));
      return withStatus.map((d) => ({
        ...d,
        highlight: d.deptId === best.deptId && best.deptId !== worst.deptId
          ? 'Top pace'
          : d.deptId === worst.deptId
            ? 'Needs attention'
            : null,
      }));
    }
    return withStatus.map((d) => ({ ...d, highlight: null }));
  }, [departments, elapsedDays, daysInMonth, tempTargets]);

  // Slowest collection day so far this month, and how many days had zero collection at
  // all (a likely data gap, not a real quiet day) - the story behind the raw table.
  const insights = useMemo(() => {
    const activeRows = modifiedRows.filter((r) => dayjs(r.date).date() <= elapsedDays);
    if (!activeRows.length) return null;
    const nonZeroRows = activeRows.filter((r) => r.total > 0);
    const worstDay = nonZeroRows.length
      ? nonZeroRows.reduce((worst, r) => (r.total < worst.total ? r : worst))
      : null;
    const zeroDaysCount = activeRows.filter((r) => r.total === 0).length;
    return { worstDay, zeroDaysCount };
  }, [modifiedRows, elapsedDays]);

  // The biggest revenue day so far - its own card at the top of the right column.
  const bestDay = useMemo(() => biggestRevenueDay(modifiedRows, { elapsedDays, departments }), [modifiedRows, elapsedDays, departments]);
  // ...and each department's own best day, in the space the daily chart used to take.
  const bestDayByDept = useMemo(() => highestDayByDepartment(modifiedRows, { elapsedDays, departments }), [modifiedRows, elapsedDays, departments]);

  // The right column sits BESIDE the table with its top and bottom lined up with the table's: while it is beside the
  // table it is given exactly the table's height, and its cards share that height out (roomy cards when they fit, the
  // compact version when they would not). When the screen is too narrow for both, the column simply drops below the
  // table at its natural height. The table itself is never touched.
  const { leftRef: tableRef, rightRef: sideRef, rightNode: sideNode, height: tableHeight, width: sideWidth, leftHeight: naturalTableHeight } = useBesideHeight();
  const beside = tableHeight !== null;
  const alertCount = (insights?.worstDay ? 1 : 0) + (insights?.zeroDaysCount > 0 ? 1 : 0);
  // Fit the table (and the column beside it) to the screen's height, like browser zoom-out: the whole block is scaled down
  // - never below 65%, never above 100% - so the month's rows are all on one screen. On a screen too small even for 65%
  // it stops there and the page scrolls a little. Everything inside is laid out at its natural size (just wider room, at
  // a smaller scale), so the column-beside-the-table logic below works exactly as at 100%.
  const { fitRef, scale } = useFitToViewport({ contentHeight: naturalTableHeight });
  const [rowRef, rowHeight] = useElementSize();
  const shrunk = scale < 1;
  const denseSide = useFitDensity({
    container: sideNode,
    active: beside,
    resetKey: [tableHeight, Math.round((sideWidth || 0) / 8), alertCount, bestDay ? 1 : 0, bestDayByDept.length, loading ? 1 : 0].join('|'),
  });

  // Temp targets roll into the Total Revenue card too, so the page stays internally
  // consistent while previewing - real revenue, target padded out with whatever's being tried.
  const grandRevenueToDate = responseData?.meta?.grandRevenueToDate || 0;
  const realGrandTarget = responseData?.meta?.grandTarget || 0;
  const tempTargetTotal = departments.reduce(
    (sum, d) => sum + (d.target === 0 ? (tempTargets[d.deptId] || 0) : 0),
    0,
  );
  const hasTempInTotal = tempTargetTotal > 0;
  const effectiveGrandTarget = realGrandTarget + tempTargetTotal;
  const effectiveGrandTAR = effectiveGrandTarget > 0
    ? parseFloat(((grandRevenueToDate / effectiveGrandTarget) * 100).toFixed(2))
    : null;

  const grandStatus = getPaceStatus({
    target: effectiveGrandTarget,
    revenueToDate: grandRevenueToDate,
    elapsedDays,
    daysInMonth,
  });
  const grandExpectedToDate = effectiveGrandTarget > 0 ? effectiveGrandTarget * (elapsedDays / daysInMonth) : 0;

  // A badge (Top pace / Needs attention) on any card gives EVERY card a badge row, so the rows below stay level
  const badgeRow = departmentsWithPace.some((d) => d.highlight);
  // A flat list (never a fragment - the grid counts its children to pick the number of columns)
  const summaryCards = loading && !responseData?.meta
    ? Array.from({ length: PLACEHOLDER_CARDS }, (_, i) => <DepartmentStatCard key={`placeholder-${i}`} loading />)
    : [
      <DepartmentStatCard
        key="total"
        title="Total Revenue (MTD)"
        revenueToDate={grandRevenueToDate}
        target={effectiveGrandTarget}
        tar={effectiveGrandTAR}
        status={grandStatus}
        expectedToDate={grandExpectedToDate}
        elapsedDays={elapsedDays}
        daysInMonth={daysInMonth}
        allowTempTarget={false}
        note={hasTempInTotal ? 'Includes temporary target(s)' : undefined}
        badgeRow={badgeRow}
      />,
      ...departmentsWithPace.map((dept) => (
        <DepartmentStatCard
          key={dept.deptId}
          deptId={dept.deptId}
          deptName={dept.deptName}
          revenueToDate={dept.revenueToDate}
          target={dept.target}
          tar={dept.tar}
          status={dept.status}
          highlight={dept.highlight}
          isTemp={dept.isTemp}
          onSetTemp={(value) => handleSetTemp(dept.deptId, value)}
          onClearTemp={() => handleClearTemp(dept.deptId)}
          allowTempTarget={dept.deptId !== 0}
          expectedToDate={dept.expectedToDate}
          elapsedDays={elapsedDays}
          daysInMonth={daysInMonth}
          badgeRow={badgeRow}
        />
      )),
    ];

  // Everything ABOVE the table - the month picker row, the headline cards, the "Daily Breakdown" title with Export - is one
  // block at the same percentage as the table (the table's own scale, READ-ONLY: nothing about the table's fitting
  // changes), but never larger than keeps the cards on ONE row, and never below the table's floor. The holder keeps the
  // scaled height so nothing below moves. See cardsScale.
  const [aboveHolderRef, , aboveWidth] = useElementSize();
  const [aboveBlockRef, aboveBlockHeight] = useElementSize();
  const cardCount = summaryCards.length;
  const oneRowWidth = cardCount * MIN_CARD_WIDTH + (cardCount - 1) * CARDS_GAP + 8; // + a little slack for rounding
  const aboveScale = cardsScale({ tableScale: scale, width: aboveWidth, oneRowWidth });
  const aboveShrunk = aboveScale < 1;

  return (
    <GlobalWrapper title="Accounts Manager" headerScale={aboveScale}>
      {/* Everything above the table in ONE block at the table's percentage (see above): the month picker row, the cards (one
          row where it fits) and the title row with Export. The outer box is the unscaled holder and keeps the scaled height. */}
      <Box ref={aboveHolderRef} sx={{ mb: 1, ...(aboveShrunk && aboveBlockHeight ? { height: aboveBlockHeight * aboveScale } : {}) }}>
        <Box
          ref={aboveBlockRef}
          data-above-scale={aboveScale}
          sx={aboveShrunk ? { transform: `scale(${aboveScale})`, transformOrigin: 'top left', width: `${100 / aboveScale}%` } : undefined}
        >
      {/* ---------- Filter bar ---------- */}
      <Box mb={1} sx={{ backgroundColor: 'white', borderRadius: '10px', p: 1.5 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={6} md={3}>
            <DatePickerCustom
              label="Month"
              value={dayjs(selectedMonth)}
              setValue={(date) => setSelectedMonth(date.format('YYYY-MM-01'))}
              viewDetails={['year', 'month']}
              fullWidth
              dateDisplayFormat="MM-YYYY"
              popupScale={aboveScale}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControlLabel
              control={<Switch checked={skillxOnly} onChange={(e) => setSkillxOnly(e.target.checked)} />}
              label="Skillx"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              {isCurrentMonth
                ? `Day ${elapsedDays} of ${daysInMonth} this month`
                : `${daysInMonth}-day month, fully elapsed`}
              {skillxOnly && ' · Showing SkillX only'}
            </Typography>
          </Grid>
        </Grid>
      </Box>

          {/* ---------- Headline + department cards ---------- */}
          {/* One card per department plus the Total, however many the backend returns; SummaryCardsGrid picks the rows so
              every card stays readable. While the first answer is awaited, placeholders hold the space so nothing jumps. */}
          <Box sx={{ mb: 1 }}>
            <SummaryCardsGrid>{summaryCards}</SummaryCardsGrid>
          </Box>

          {/* ---------- Daily breakdown table ---------- */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Daily Breakdown
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<FileDownloadRoundedIcon />}
              onClick={handleExport}
              disabled={!modifiedRows.length}
            >
              Export
            </Button>
          </Box>
        </Box>
      </Box>

          {/* Table at its natural width, the analysis column beside it (same top, same height); the column wraps below
              the table when the screen is too narrow for both. */}
      {/* the unscaled box that holds the (possibly scaled) block: it keeps the scaled height so nothing below moves */}
      <Box ref={fitRef} sx={shrunk && rowHeight ? { height: rowHeight * scale } : undefined}>
        <Box
          ref={rowRef}
          data-fit-scale={scale}
          sx={{
            position: 'relative', // the table and the column share this as their offsetParent (see useBesideHeight)
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 1.5,
            ...(shrunk ? { transform: `scale(${scale})`, transformOrigin: 'top left', width: `${100 / scale}%` } : {}),
          }}
        >
            {/* 60 / 40: the table takes 60% of the row, the analysis column 40% (the 12px gap is split between them);
                below md they stack, each full width */}
            <Box ref={tableRef} sx={{ flex: { xs: '1 1 100%', md: '0 0 calc(60% - 6px)' }, minWidth: 0 }}>
              <DailyBreakdownTable headers={headers} rows={modifiedRows} footerRow={monthTotalsRow} loading={loading} />
            </Box>
            {/* Right column: the biggest revenue day, the other insight alerts, each department's best day, then where the revenue came from (the donut). */}
            <Box
              ref={sideRef}
              data-layout={beside ? (denseSide ? 'beside-compact' : 'beside-roomy') : 'below'}
              sx={{
                flex: { xs: '1 1 100%', md: '0 0 calc(40% - 6px)' },
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignSelf: 'stretch',
                // beside the table: exactly its height (scrolls only if even the compact cards cannot fit - a safety net)
                ...(beside ? { height: tableHeight, overflowY: 'auto' } : {}),
              }}
            >
              <BiggestDayCard best={bestDay} loading={loading && !responseData?.meta} dense={denseSide} />
              {insights && (
                <Stack spacing={1} sx={{ flex: '0 0 auto' }}>
                  {insights.worstDay && (
                    <Alert icon={<TrendingDownRoundedIcon fontSize="small" />} severity="info">
                      Slowest day so far: <b>{insights.worstDay.dayLabel}</b> — {formatCurrency(insights.worstDay.total)}
                    </Alert>
                  )}
                  {insights.zeroDaysCount > 0 && (
                    <Alert icon={<EventBusyRoundedIcon fontSize="small" />} severity="warning">
                      {insights.zeroDaysCount} day{insights.zeroDaysCount > 1 ? 's' : ''} with zero collection recorded
                    </Alert>
                  )}
                </Stack>
              )}
              {/* Each department's highest income day of the month. */}
              <HighestDayByDepartment highest={bestDayByDept} loading={loading && !responseData?.meta} dense={denseSide} sx={beside ? { flex: '1 0 auto' } : undefined} />
              {/* Where this month's revenue came from, by department - same look as the Refund Reasons Breakdown donut. */}
              <DepartmentShareDonut
                departments={departmentsWithPace}
                total={grandRevenueToDate}
                dailyAverage={elapsedDays > 0 ? grandRevenueToDate / elapsedDays : 0}
                loading={loading && !responseData?.meta}
                dense={denseSide}
                sx={beside ? { flex: '1.4 0 auto' } : undefined}
              />
            </Box>
        </Box>
      </Box>

      <IncomeDetailDialog
        open={!!detailCell}
        onClose={() => setDetailCell(null)}
        date={detailCell?.date}
        deptId={detailCell?.deptId ?? null}
        deptName={detailCell?.deptName}
        month={selectedMonth}
        skillxOnly={skillxOnly}
        popupScale={scale} // the table's own scale: the popup keeps the proportion of the table it was opened from
      />
    </GlobalWrapper>
  );
}
